import { Injectable } from '@angular/core';
import { InvoiceHeader } from '../../models/invoice.model';
import {
  InvoiceDashboardStat,
  InvoiceMarketingDashboardDto,
  InvoiceMarketingDashboardView,
  InvoiceRecentActivityItem,
  InvoiceTableDto,
  InvoiceTrendChartDto,
  InvoiceTrendChartView,
} from '../../models/finance/invoice-list.model';

@Injectable({
  providedIn: 'root',
})
export class MapperInvoiceListService {
  mapTableFromDto(dto: InvoiceTableDto): {
    items: InvoiceHeader[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as InvoiceTableDto & Record<string, unknown>;
    return {
      items: ((dto.data ?? raw['Data'] ?? []) as InvoiceHeader[]).map(item => this.normalizeRow(item)),
      total: dto.total ?? (raw['Total'] as number) ?? 0,
      page: dto.page ?? (raw['Page'] as number) ?? 1,
      pageSize: dto.pageSize ?? (raw['PageSize'] as number) ?? 20,
      totalPages: dto.totalPages ?? (raw['TotalPages'] as number) ?? 0,
    };
  }

  mapSearchField(type: 'customerName' | 'invoiceNo'): string {
    return type === 'invoiceNo' ? 'invoice_no' : 'customer_name';
  }

  mapStatusFilter(filterStatus: number | 'all'): 'all' | 'pending' | 'paid' | undefined {
    switch (filterStatus) {
      case 1:
        return 'pending';
      case 2:
        return 'paid';
      default:
        return undefined;
    }
  }

  mapSortColumn(column: string): string {
    switch (column) {
      case 'invoiceNo':
        return 'invoiceno';
      case 'customerName':
        return 'customername';
      case 'total':
        return 'total';
      case 'remaining':
        return 'remaining';
      case 'invoiceDate':
        return 'invoicedate';
      case 'dueDate':
        return 'duedate';
      case 'status':
        return 'status';
      default:
        return 'id';
    }
  }

  mapMarketingDashboard(dto: InvoiceMarketingDashboardDto): InvoiceMarketingDashboardView {
    const raw = dto as InvoiceMarketingDashboardDto & Record<string, unknown>;
    const summaryRaw = dto.summary ?? raw['Summary'];
    const ratiosRaw = dto.ratios ?? raw['Ratios'];
    const recentRaw = (dto.recentActivity ?? raw['RecentActivity'] ?? []) as InvoiceRecentActivityItem[];
    const summary = this.normalizeSummary(summaryRaw);
    const ratiosAny = ratiosRaw as unknown as Record<string, number> | undefined;

    return {
      stats: this.mapDashboardStats(summary),
      summary: {
        totalAmount: summary.totalAmount,
        paidAmount: summary.paidAmount,
        pendingAmount: summary.pendingAmount,
        overdueAmount: summary.overdueAmount,
      },
      ratios: {
        paidPercent: ratiosRaw?.paidPercent ?? ratiosAny?.['PaidPercent'] ?? 0,
        pendingPercent: ratiosRaw?.pendingPercent ?? ratiosAny?.['PendingPercent'] ?? 0,
        overduePercent: ratiosRaw?.overduePercent ?? ratiosAny?.['OverduePercent'] ?? 0,
      },
      recentActivity: recentRaw.map(item => this.mapRecentActivity(item)),
    };
  }

  mapTrendChart(dto: InvoiceTrendChartDto): InvoiceTrendChartView {
    const raw = dto as InvoiceTrendChartDto & Record<string, unknown>;
    const pointsRaw = (dto.points ?? raw['Points'] ?? []) as unknown as Array<Record<string, unknown>>;

    return {
      mode: (dto.mode ?? raw['Mode'] ?? 'daily') as 'daily' | 'monthly',
      rangeStart: String(dto.rangeStart ?? raw['RangeStart'] ?? ''),
      rangeEnd: String(dto.rangeEnd ?? raw['RangeEnd'] ?? ''),
      totalCount: Number(dto.totalCount ?? raw['TotalCount'] ?? 0),
      points: pointsRaw.map(point => ({
        periodStart: String(point['periodStart'] ?? point['PeriodStart'] ?? ''),
        label: String(point['label'] ?? point['Label'] ?? ''),
        subLabel: (point['subLabel'] ?? point['SubLabel'] ?? null) as string | null,
        count: Number(point['count'] ?? point['Count'] ?? 0),
        amount: Number(point['amount'] ?? point['Amount'] ?? 0),
      })),
    };
  }

  resolveDisplayStatus(item: InvoiceHeader): number {
    const raw = item as InvoiceHeader & Record<string, unknown>;
    const displayStatus = raw['displayStatus'] ?? raw['DisplayStatus'];
    if (displayStatus !== undefined && displayStatus !== null) {
      return Number(displayStatus);
    }
    if (item.status === 1 && item.dueDate) {
      const due = new Date(item.dueDate);
      if (!isNaN(due.getTime()) && due.getTime() < Date.now()) {
        return 4;
      }
    }
    return item.status;
  }

  private mapDashboardStats(summary: ReturnType<MapperInvoiceListService['normalizeSummary']>): InvoiceDashboardStat[] {
    return [
      {
        title: 'ยอดรวมทั้งหมด',
        value: summary.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        icon: 'receipt_long',
        color: 'text-indigo-600',
      },
      {
        title: 'ชำระแล้ว',
        value: summary.paidAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        icon: 'check_circle',
        color: 'text-emerald-500',
      },
      {
        title: 'รอชำระ',
        value: summary.pendingAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        icon: 'pending_actions',
        color: 'text-amber-500',
      },
      {
        title: 'เกินกำหนด',
        value: summary.overdueAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        icon: 'error_outline',
        color: 'text-rose-500',
      },
    ];
  }

  private normalizeRow(item: InvoiceHeader): InvoiceHeader {
    const raw = item as InvoiceHeader & Record<string, unknown>;
    const fee = Number(item.fee ?? raw['Fee'] ?? 0);
    const vat = Number(item.vat ?? raw['Vat'] ?? 0);
    const total = Number(raw['total'] ?? raw['Total'] ?? fee + vat);
    const normalized: InvoiceHeader = {
      id: item.id ?? (raw['Id'] as number) ?? 0,
      name: item.name ?? (raw['Name'] as string) ?? '',
      invoiceNo: item.invoiceNo ?? (raw['InvoiceNo'] as string) ?? '',
      siteName: item.siteName ?? (raw['SiteName'] as string) ?? '',
      invoiceDate: item.invoiceDate ?? (raw['InvoiceDate'] as Date) ?? undefined,
      remark: item.remark ?? (raw['Remark'] as string) ?? '',
      fee,
      vat,
      status: item.status ?? (raw['Status'] as number) ?? 0,
      dueDate: item.dueDate ?? (raw['DueDate'] as Date) ?? undefined as unknown as Date,
      customerName: item.customerName ?? (raw['CustomerName'] as string) ?? '',
      createBy: item.createBy ?? (raw['CreateBy'] as string) ?? '',
    };
    (normalized as InvoiceHeader & { displayStatus?: number }).displayStatus =
      Number(raw['displayStatus'] ?? raw['DisplayStatus'] ?? normalized.status);
    return normalized;
  }

  private normalizeSummary(summary: unknown) {
    const raw = (summary ?? {}) as Record<string, number>;
    return {
      totalAmount: raw['totalAmount'] ?? raw['TotalAmount'] ?? 0,
      paidAmount: raw['paidAmount'] ?? raw['PaidAmount'] ?? 0,
      pendingAmount: raw['pendingAmount'] ?? raw['PendingAmount'] ?? 0,
      overdueAmount: raw['overdueAmount'] ?? raw['OverdueAmount'] ?? 0,
      totalCount: raw['totalCount'] ?? raw['TotalCount'] ?? 0,
      paidCount: raw['paidCount'] ?? raw['PaidCount'] ?? 0,
      pendingCount: raw['pendingCount'] ?? raw['PendingCount'] ?? 0,
      overdueCount: raw['overdueCount'] ?? raw['OverdueCount'] ?? 0,
    };
  }

  private mapRecentActivity(item: InvoiceRecentActivityItem) {
    const raw = item as InvoiceRecentActivityItem & Record<string, unknown>;
    const customerName = item.customerName ?? (raw['CustomerName'] as string) ?? '';
    const invoiceNo = item.invoiceNo ?? (raw['InvoiceNo'] as string) ?? '';
    const amount = Number(item.amount ?? raw['Amount'] ?? 0);
    const id = item.id ?? (raw['Id'] as number) ?? 0;
    const name = customerName || invoiceNo || 'ไม่ระบุชื่อ';

    return {
      id,
      initial: name.charAt(0).toUpperCase(),
      name,
      timeAgo: '',
      info: `฿ ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      color: '',
      invoiceDate: item.invoiceDate ?? (raw['InvoiceDate'] as string) ?? null,
    };
  }
}
